<script setup lang="ts">
import { computed } from 'vue'

import BetaSettingModeSwitch from '@/components/beta-editor/settings/controls/BetaSettingModeSwitch.vue'

const props = withDefaults(defineProps<{
    label: string
    value?: string | number | null
    valueDefault?: string | number | null
    lock?: boolean
    showSwitch?: boolean
}>(), {
    value: null,
    valueDefault: null,
    lock: true,
    showSwitch: false,
})

const emit = defineEmits(['change'])

const effectiveValue = computed(() => (
    props.lock ? (props.valueDefault ?? props.value) : props.value
))

const options = [
    { value: '100', label: 'thin' },
    { value: '200', label: 'extra-light' },
    { value: '300', label: 'light' },
    { value: '400', label: 'normal' },
    { value: '500', label: 'medium' },
    { value: '600', label: 'semi-bold' },
    { value: '700', label: 'bold' },
    { value: '800', label: 'extra-bold' },
    { value: '900', label: 'black' },
]

const onChangeFontWeight = (event: any) => {
    emit('change', {
        target: 'font-weight',
        value: event.target.value,
    })
}

const onChangeLock = () => {
    emit('change', {
        target: 'style_font_weight_lock',
        value: !props.lock,
    })
}
</script>

<template>
    <div class="space-y-2">
        <div class="flex w-full items-center justify-between">
            <span class="text-sm font-semibold text-[#060606] opacity-50">{{ label }}</span>

            <beta-setting-mode-switch
                v-if="showSwitch"
                :active="lock"
                @toggle="onChangeLock"
            />

            <button v-else type="button" @click="onChangeLock">
                <span class="sr-only">Alternar herança</span>
                <svg v-if="lock" class="h-[14px] w-[14px] stroke-[#060606]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor">
                    <path d="M3.85005 5.13336V4.60002C3.85005 2.82733 5.25505 1.40002 7.00005 1.40002C8.74505 1.40002 10.15 2.82733 10.15 4.60002V5.13336M3.85005 5.13336C3.27255 5.13336 2.80005 5.61336 2.80005 6.20002V11.5334C2.80005 12.12 3.27255 12.6 3.85005 12.6H10.15C10.7275 12.6 11.2 12.12 11.2 11.5334V6.20002C11.2 5.61336 10.7273 5.13336 10.15 5.13336M3.85005 5.13336H10.15" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <svg v-else class="h-[14px] w-[14px] stroke-[#060606]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none">
                    <g opacity="0.1">
                        <path d="M3.71855 4.5999C3.71855 2.8272 5.12355 1.3999 6.86855 1.3999C7.95555 1.3999 8.91062 1.95374 9.47633 2.7999M3.8498 5.13324C3.2723 5.13324 2.7998 5.61324 2.7998 6.1999V11.5332C2.7998 12.1199 3.2723 12.5999 3.8498 12.5999H10.1498C10.7273 12.5999 11.1998 12.1199 11.1998 11.5332V6.1999C11.1998 5.61324 10.7273 5.13324 10.1498 5.13324H3.8498Z" stroke-width="1.5" stroke-linecap="round"/>
                    </g>
                </svg>
            </button>
        </div>

        <select
            class="w-full appearance-none rounded border-2 border-[#F5F5F5] bg-[#fcfcfc] px-4 py-3 text-sm outline-none"
            :disabled="lock"
            @change="onChangeFontWeight"
        >
            <option
                v-for="option in options"
                :key="option.value"
                :value="option.value"
                :selected="String(option.value) === String(effectiveValue ?? '')"
            >
                {{ option.label }}
            </option>
        </select>
    </div>
</template>
